import { useBundle } from '@amzn/react-arb-tools';
import { WarningOutlined } from '@ant-design/icons';
import type {
  AdditionalHts,
  UsConsumptionEntryCensusWarning,
} from '@xbcb/api-gateway-client';
import { isRealHts } from '@xbcb/entry-utils/dist/lib/htsUtil';
import { formatHTS, orderTariffs } from '@xbcb/hts-client-utils';
import {
  AccountType,
  AnyObject,
  WorkOrderRecordType,
} from '@xbcb/shared-types';
import { getEnv } from '@xbcb/ui-env';
import type { NamePath } from '@xbcb/ui-types';
import { UiStage } from '@xbcb/ui-types';
import { getCodes, safeGetMessage } from '@xbcb/ui-utils';
import { WarningStatus } from '@xbcb/work-order-types';
import { UsPgaFlag, type ModeOfTransport } from '__generated__/graphql';
import { Form, Tabs } from 'antd';
import AluminumSmeltAndCastCountryDetail from 'components/AluminumSmeltAndCastCountryDetail';
import CopperSmeltAndCastCountryDetail from 'components/CopperSmeltAndCastCountryDetail';
import LineInfoTab from 'components/LineInfoTab';
import LineItemCase from 'components/LineItemCase';
import LineItemCensusWarningsTab from 'components/LineItemCensusWarningsTab';
import LineItemFeeTab from 'components/LineItemFeeTab';
import LineItemLicenseInfoTab from 'components/LineItemLicenseInfoTab';
import LineItemTariffTab from 'components/LineItemTariffTab';
import ProductUsageTab from 'components/ProductUsageTab';
import { getMergedQuestions } from 'components/ProductUsageTab/config';
import SteelMeltAndPourCountryDetail from 'components/SteelMeltAndPourCountryDetail';
import { useAppRecordContext } from 'context/appRecordContext';
import { useHtsValues } from 'context/htsValues';
import {
  isHydraSplitCipl301And232Enabled,
  isProject42Enabled,
  isSection232CopperEnabled,
  isTariff232AutomationFeatureEnabled,
  isTariffRedesignEnabled,
} from 'featureFlags';
import { getAdCvdCases } from 'libs/adCvdHelpers';
import { useEntryValidation, ValidationErrors } from 'libs/entryValidation';
import {
  useArrivalDate,
  useCurrentUser,
  useHtsQuery,
  usePrevious,
  useWorkOrderTaskConfiguration,
} from 'libs/hooks';
import {
  getAdditionalGrouped,
  isAdditionalHtsApplicable,
  isSection232AluminumSmeltAndCastApplicableForAnyTariff,
  isSection232CopperSmeltAndCastApplicableForAnyTariff,
  isSection232SteelMeltAndPourApplicableForAnyTariff,
} from 'libs/htsHelpers';
import { HTS_INDICES } from 'libs/htsIndices';
import { getLineItemPgaFlags, getLineItemPgaTabs } from 'libs/pgaHelpers';
import { isReadOnly, shouldRequire } from 'libs/readWriteControl';
import type { RemoveAdditionalTariff } from 'libs/tariffs';
import { addAdditionalTariffs, removeAdditionalTariff } from 'libs/tariffs';
import { cloneDeep, get, isEqual } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CommercialInvoiceBaseProps, TabElement, Tariff } from 'types';
import { StyledTabContainer } from './styles';

const EMPTY_CANDIDATE_TARIFFS: { number: string; type?: string | null }[] = [];

export interface LineItemTabsProps extends CommercialInvoiceBaseProps {
  keyRef: React.MutableRefObject<string>;
  tariffs?: Tariff[];
}

const { stage } = getEnv();

/**
 * We want to indicate to users if there are validation errors or missing
 * fields for a given tab to guide them to what needs to be fixed.
 */

export const tabTitleGenerator =
  (validator: ValidationErrors | null) =>
  (
    baseTitle: string,
    namePathsToValidate: NamePath[] = [],
    excludedKeys: string[] = [],
  ): React.ReactElement | string => {
    if (!validator) return baseTitle;
    const tabHasErrors = namePathsToValidate.some(
      (namePath) =>
        validator.hasErrors({ path: namePath, excludedKeys }) ||
        validator.hasMissingFields({ path: namePath, excludedKeys }),
    );
    return (
      <>
        {baseTitle} {tabHasErrors ? <WarningOutlined /> : undefined}
      </>
    );
  };

export const LineItemTabs: React.FC<LineItemTabsProps> = ({
  readOnly,
  disabled,
  required,
  fullNamePath,
  localNamePath,
  workOrderType,
  keyRef,
  tariffs = [],
  isInboundBooking,
}) => {
  const [bundle] = useBundle('components.ProductUsageTab');
  const { validator, validateFields } = useEntryValidation(fullNamePath);
  const getTabTitle = tabTitleGenerator(validator);
  const { taskActivelyInProgress } = useAppRecordContext();
  const [adTabManuallyAdded, setAdTabManuallyAdded] = useState(false);
  const [cvTabManuallyAdded, setCvTabManuallyAdded] = useState(false);
  const [manuallyRemovedTariffs, setManuallyRemovedTariffs] = useState<
    string[]
  >([]);
  const { account, accountType } = useCurrentUser();
  const { readWriteControlsConfig } = useWorkOrderTaskConfiguration();
  const arrivalDate = useArrivalDate();
  const { getEffectiveUsHtssData } = useHtsQuery();
  const htsData = getEffectiveUsHtssData(arrivalDate);

  const codes = getCodes();
  const { CATAIR: CATAIRCodes = {} } = get(codes, 'CBP', {});
  const { fee: feeCodes = {}, taxClass: taxClassCodes = {} } = CATAIRCodes;

  const form = Form.useFormInstance();
  const { getFieldValue } = form;
  const candidateAdditionalTariffs: {
    number: string;
    type?: string | null;
  }[] =
    Form.useWatch([...fullNamePath, 'candidateAdditionalTariffs'], form) ??
    EMPTY_CANDIDATE_TARIFFS;
  const originCountryCode = Form.useWatch(
    [...fullNamePath, 'origin', 'countryCode'],
    form,
  );
  const modeOfTransport: ModeOfTransport | undefined = Form.useWatch(
    ['conveyance', 'modeOfTransport'],
    form,
  );
  const productCharacteristics = Form.useWatch(
    [...fullNamePath, 'productCharacteristics'],
    form,
  );
  const shipperId = Form.useWatch(['group', 'shipper', 'id'], {
    form,
    preserve: true,
  });
  const createdTime = Form.useWatch(['group', 'shipment', 'created', 'time'], {
    form,
    preserve: true,
  });

  const previousOriginCode = usePrevious(originCountryCode);
  const { htsValues } = useHtsValues();
  const prevHtsValues = usePrevious(htsValues);

  const isType86 = workOrderType === WorkOrderRecordType.US_TYPE86_ENTRY;
  const isPsc =
    workOrderType === WorkOrderRecordType.US_POST_SUMMARY_CORRECTION;

  const isP42 = isProject42Enabled({ shipperId: account?.id });

  const allPGAFlags = useMemo(() => {
    if (isTariffRedesignEnabled) {
      return tariffs.map((tariff: any) => {
        if (!tariff?.htsNumber) return [];
        const htsObj = htsData[formatHTS(tariff.htsNumber)];
        const normalPGAFlags = htsObj?.pga ?? [];
        const extraPgaFlags = tariff.extraPgaFlags ?? [];
        const flagSet = new Set<UsPgaFlag>([
          ...normalPGAFlags,
          ...extraPgaFlags,
        ]);
        if (tariff && 'pga' in tariff && tariff.pga) {
          Object.keys(tariff.pga)
            .filter((key) => key !== '__typename' && tariff.pga[key])
            .forEach((key) => flagSet.add(key as UsPgaFlag));
        }
        return [...flagSet];
      });
    }
    return getLineItemPgaFlags(htsValues, tariffs);
  }, [htsValues, tariffs, htsData]);

  const handleRemoveTariff = useCallback<RemoveAdditionalTariff>(
    async (tariffIndex, form, fullNamePath, htsValues) => {
      // We need to remove the tariff but also mark it as manually removed
      // to prevent auto-adding it on the next iteration.
      setManuallyRemovedTariffs((previousRemovedTariffs) => [
        ...previousRemovedTariffs,
        formatHTS(htsValues.htsNumbers[tariffIndex]),
      ]);
      return await removeAdditionalTariff(
        tariffIndex,
        form,
        fullNamePath,
        htsValues,
      );
    },
    [setManuallyRemovedTariffs],
  );

  const addAdditionalApplicableTariffs = useCallback(
    async (htsValues: AnyObject) => {
      if (
        isType86 || // T86 doesn't use additional HTS
        !htsValues.htsObjects[0] ||
        !originCountryCode ||
        !isRealHts(htsValues.htsObjects[0]?.number)
      ) {
        return;
      }
      let additionalGrouped = getAdditionalGrouped(
        htsValues.htsObjects[0],
        originCountryCode,
      ).filter((additional) =>
        isAdditionalHtsApplicable(additional, manuallyRemovedTariffs),
      );

      if (stage === UiStage.PROD) {
        // Mimic existing prod behavior
        // Pending ops sign off on https://app.asana.com/1/8442528107068/project/1210150806086495/task/1209517261829877
        additionalGrouped = additionalGrouped.slice(0, 1);
      }

      await addAdditionalTariffs(additionalGrouped, form, fullNamePath);
    },
    [originCountryCode, isType86, manuallyRemovedTariffs],
  );

  // Clean up undefined elements from tariffs array
  useEffect(() => {
    if (isTariffRedesignEnabled) return;
    const cleanupTariffs = () => {
      const currTariffs =
        form.getFieldValue([...fullNamePath, 'tariffs']) ?? [];
      const validTariffs = currTariffs.filter(
        (tariff: any) => tariff != null && tariff.htsNumber,
      );
      if (currTariffs.length !== validTariffs.length) {
        form.setFieldValue([...fullNamePath, 'tariffs'], validTariffs);
      }
    };
    cleanupTariffs();
  }, [form, fullNamePath, tariffs]);

  useEffect(() => {
    const reorderTariffs = () => {
      const htsDataMap: any = {};
      const currTariffs =
        form.getFieldValue([...fullNamePath, 'tariffs']) ?? [];
      for (let i = 0; i < htsValues.htsObjects.length; i++) {
        htsDataMap[formatHTS(htsValues.htsObjects[i]?.number)] =
          htsValues.htsObjects[i];
      }

      const sortedTariffs = orderTariffs(cloneDeep(currTariffs), htsDataMap);
      if (isEqual(currTariffs, sortedTariffs)) return;

      form.setFieldValue([...fullNamePath, 'tariffs'], sortedTariffs);
    };

    // Pending Ops sign-off on https://app.asana.com/1/8442528107068/project/1210150806086495/task/1209517261829877
    if (stage !== UiStage.PROD && !isTariffRedesignEnabled) {
      reorderTariffs();
    }
  }, [form, tariffs, fullNamePath, htsValues, prevHtsValues]);

  // When user changes inputted HTS numbers we should remove any additional tariffs that were added to a number that no longer exists.
  // Once stale additional tariffs are removed, any additional tariffs that apply to primary HTS are auto-added.
  useEffect(() => {
    const updateAdditionalTariffsOnHtsChange = async () => {
      // Tariff redesign owns additional-HTS add/remove; skip legacy orchestration.
      if (isTariffRedesignEnabled) {
        return;
      }
      if (!prevHtsValues) {
        return;
      }
      if (isEqual(prevHtsValues?.htsNumbers, htsValues.htsNumbers)) {
        return;
      }
      if ((!isP42 && accountType !== AccountType.OPERATOR) || readOnly) {
        return;
      }
      const newHtsNumbers = HTS_INDICES.map((index) => {
        return formatHTS(htsValues.htsNumbers[index]);
      });
      for (const index of [1, 2, 3, 4, 5, 6, 7]) {
        if (
          prevHtsValues?.htsNumbers?.[index] &&
          prevHtsValues?.htsObjects?.[index] &&
          !newHtsNumbers.includes(formatHTS(prevHtsValues.htsNumbers[index]))
        ) {
          if (prevHtsValues?.htsObjects[index]?.additional) {
            const additionalNumbers =
              prevHtsValues?.htsObjects[index]?.additional?.map(
                (additionalHts: AdditionalHts) => {
                  return formatHTS(additionalHts.number);
                },
              ) ?? [];
            if (
              additionalNumbers.includes(
                formatHTS(htsValues.htsNumbers[index - 1]),
              )
            ) {
              await handleRemoveTariff(
                index - 1,
                form,
                fullNamePath,
                htsValues,
              );
            }
          }
        }
      }
      await addAdditionalApplicableTariffs(htsValues);
    };
    void updateAdditionalTariffsOnHtsChange();
  }, [
    htsValues,
    accountType,
    readOnly,
    addAdditionalApplicableTariffs,
    prevHtsValues,
    fullNamePath,
    form,
    handleRemoveTariff,
  ]);

  const tabs: TabElement[] = useMemo(() => {
    const tabs: TabElement[] = [
      // Line info tab is always visible
      {
        key: 'line-info',
        // The fields rendered by LineInfo aren't nested in form state
        label: getTabTitle('Line Info', [
          [...fullNamePath, 'description'],
          [...fullNamePath, 'poNumber'],
          [...fullNamePath, 'origin'],
          [...fullNamePath, 'manufacturer'],
          [...fullNamePath, 'lineReasons'],
          [...fullNamePath, 'spi'],
        ]),
        forceRender: true,
        children: (
          <LineInfoTab
            fullNamePath={fullNamePath}
            localNamePath={localNamePath}
            workOrderType={workOrderType}
            removeAdditionalTariff={handleRemoveTariff}
            readOnly={readOnly}
            disabled={disabled}
            required={required}
          />
        ),
      },
    ];

    if (htsValues.licenseCode) {
      tabs.push({
        key: 'licenseInfo',
        label: getTabTitle('License Info', [
          [...fullNamePath, 'licenseNumber'],
        ]),
        forceRender: true,
        children: (
          <LineItemLicenseInfoTab
            fullNamePath={fullNamePath}
            localNamePath={localNamePath}
            readOnly={readOnly}
            disabled={disabled}
            required={required}
            licenseCode={htsValues.licenseCode}
          />
        ),
      });
    }

    const lineFees = feeCodes['62'] ?? {};
    const fees = get(htsValues.lineHTSObj, 'fees', []) ?? [];
    const feeTabs = fees
      .filter((fee) => Boolean(fee.class))
      .map((fee, i) => {
        const feeClass = fee.class ?? '';
        const lineFee = lineFees[feeClass];
        const taxClass = taxClassCodes[feeClass];
        return {
          key: feeClass,
          label: getTabTitle(lineFee || taxClass, [
            [...fullNamePath, 'feeOptions'],
            [...fullNamePath, 'taxOptions'],
          ]),
          forceRender: true,
          children: (
            <LineItemFeeTab
              fee={fee}
              index={i}
              lineFee={lineFee}
              taxClass={taxClass}
              fullNamePath={[...fullNamePath]}
              localNamePath={[...localNamePath]}
              readOnly={readOnly}
              disabled={disabled}
              required={required}
            />
          ),
        };
      });
    tabs.push(...feeTabs);

    // AD/CVD case tabs show if there are any corresponding cases, or if
    // the user has manually toggled them on in the Tariff tab
    const { adCases, cvCases } = getAdCvdCases(htsValues, originCountryCode);
    const adTabVisible = Boolean(
      adCases.length ||
        adTabManuallyAdded ||
        getFieldValue(fullNamePath)?.adCase,
    );
    const cvTabVisible = Boolean(
      cvCases.length ||
        cvTabManuallyAdded ||
        getFieldValue(fullNamePath)?.cvCase,
    );

    if (adTabVisible) {
      tabs.push({
        key: 'ad',
        label: getTabTitle('Anti-Dumping Duty', [[...fullNamePath, 'adCase']]),
        forceRender: true,

        children: (
          <LineItemCase
            key={keyRef?.current}
            type="ad"
            recordType={workOrderType}
            principalAdCvdCases={adCases}
            htsNumber={htsValues.lineHtsNumber ?? ''}
            readOnly={
              readOnly ||
              isReadOnly(
                'invoices.products.lines.adCase',
                readWriteControlsConfig,
              )
            }
            required={shouldRequire(
              'invoices.products.lines.adCase',
              readWriteControlsConfig,
              required,
            )}
            disabled={disabled}
            fullNamePath={[...fullNamePath, 'adCase']}
            localNamePath={[...localNamePath, 'adCase']}
          />
        ),
      });
    }

    if (cvTabVisible) {
      tabs.push({
        key: 'cv',
        label: getTabTitle('Countervailing Duty', [
          [...fullNamePath, 'cvCase'],
        ]),
        forceRender: true,
        children: (
          <LineItemCase
            key={keyRef?.current}
            type="cv"
            recordType={workOrderType}
            principalAdCvdCases={cvCases}
            htsNumber={htsValues.lineHtsNumber ?? ''}
            readOnly={
              readOnly ||
              isReadOnly(
                'invoices.products.lines.cvCase',
                readWriteControlsConfig,
              )
            }
            required={shouldRequire(
              'invoices.products.lines.cvCase',
              readWriteControlsConfig,
              required,
            )}
            disabled={disabled}
            fullNamePath={[...fullNamePath, 'cvCase']}
            localNamePath={[...localNamePath, 'cvCase']}
          />
        ),
      });
    }

    // If an HTS number(s) is/are selected, we show a tab for each one.
    const isFirstHTS = (i: number) => i === 0;
    const hasAdditionalHTS = (i: number) =>
      get(htsValues.htsObjects, [i - 1, 'additionalHTS']);

    if (isTariffRedesignEnabled) {
      const tariffTabs = tariffs
        .map((t: any, i: number) => {
          if (!t?.htsNumber) return null;
          const htsNumber = t.htsNumber as string;
          if (!htsData[formatHTS(htsNumber)]) return null;
          return {
            key: `${htsNumber}-${i}`,
            label: getTabTitle(
              htsNumber,
              [[...fullNamePath, 'tariffs', i]],
              ['pga'],
            ),
            forceRender: true,
            children: (
              <LineItemTariffTab
                index={i}
                key={`${htsNumber}-${i}`}
                fullNamePath={[...fullNamePath, 'tariffs', i]}
                localNamePath={[...localNamePath, 'tariffs', i]}
                workOrderType={workOrderType}
                readOnly={readOnly}
                disabled={disabled}
                required={required && isRealHts(htsNumber)}
                setAdTabManuallyAdded={setAdTabManuallyAdded}
                setCvTabManuallyAdded={setCvTabManuallyAdded}
                adTabVisible={adTabVisible}
                cvTabVisible={cvTabVisible}
              />
            ),
          };
        })
        .filter((t): t is NonNullable<typeof t> => t !== null);
      tabs.push(...tariffTabs);
    } else {
      const tariffTabs = htsValues?.htsObjects
        .filter((htsObj, i) => (isFirstHTS(i) || hasAdditionalHTS(i)) && htsObj)
        .map((_, i) => {
          const htsNumber = htsValues.htsNumbers[i] as string;
          return {
            key: htsNumber,
            label: getTabTitle(
              htsNumber,
              [[...fullNamePath, 'tariffs', i]],
              ['pga'],
            ),
            forceRender: true,
            children: (
              <LineItemTariffTab
                index={i}
                key={htsNumber}
                fullNamePath={[...fullNamePath, 'tariffs', i]}
                localNamePath={[...localNamePath, 'tariffs', i]}
                workOrderType={workOrderType}
                readOnly={readOnly}
                disabled={disabled}
                required={required && isRealHts(htsNumber)}
                setAdTabManuallyAdded={setAdTabManuallyAdded}
                setCvTabManuallyAdded={setCvTabManuallyAdded}
                adTabVisible={adTabVisible}
                cvTabVisible={cvTabVisible}
              />
            ),
          };
        });
      tabs.push(...tariffTabs);
    }

    // Any PGA flags on the selected tariffs and/or manually added receive a tab.
    // PGA values live at the tariff level in the form state, even though
    // all the tabs are inline at the top (this) level.
    if (!isPsc) {
      const pgaTabs = getLineItemPgaTabs(allPGAFlags, {
        readOnly,
        required,
        localNamePath,
        fullNamePath,
        disabled,
        getTabTitle,
        shipperId,
        createdTime: arrivalDate,
        tariffs,
      });
      tabs.push(...pgaTabs.flat());
    }

    const censusWarnings: UsConsumptionEntryCensusWarning[] =
      getFieldValue([...fullNamePath, 'censusWarnings']) ?? [];
    // Warnings that have been resolved without an override code
    // are not displayed in the Census Warnings tab
    const hasDisplayableCensusWarnings = censusWarnings.some(
      (warning) =>
        warning.warningStatus === WarningStatus.ACTIVE ||
        Boolean(warning.overrideCode),
    );
    if (hasDisplayableCensusWarnings) {
      tabs.push({
        key: 'censusWarnings',
        label: getTabTitle('Census Warnings', [
          [...fullNamePath, 'censusWarnings'],
        ]),
        forceRender: true,
        children: (
          <LineItemCensusWarningsTab
            localNamePath={localNamePath}
            warnings={censusWarnings}
            readOnly={!taskActivelyInProgress}
          />
        ),
      });
    }

    // A Hydra master booking has no import date, so without this the metal
    // tabs never render and their fields stay unreachable.
    const skipSection232ImportDateCheck =
      isHydraSplitCipl301And232Enabled && Boolean(isInboundBooking);

    const shouldShowAluminumInfoTab = () =>
      isSection232AluminumSmeltAndCastApplicableForAnyTariff(
        htsValues.htsObjects,
        arrivalDate ? new Date(arrivalDate) : undefined,
        skipSection232ImportDateCheck,
      );

    if (shouldShowAluminumInfoTab()) {
      tabs.push({
        key: 'aluminumInfo',
        label: getTabTitle('Aluminum Info', [
          [...fullNamePath, 'aluminumRequirements'],
        ]),
        forceRender: true,
        children: (
          <AluminumSmeltAndCastCountryDetail
            fullNamePath={fullNamePath}
            localNamePath={localNamePath}
          />
        ),
      });
    }

    const shouldShowSteelInfoTab = () =>
      isSection232SteelMeltAndPourApplicableForAnyTariff(
        htsValues.htsObjects,
        arrivalDate ? new Date(arrivalDate) : undefined,
        skipSection232ImportDateCheck,
      );

    if (shouldShowSteelInfoTab()) {
      tabs.push({
        key: 'steelInfo',
        label: getTabTitle('Steel Info', [
          [...fullNamePath, 'steelRequirements'],
        ]),
        forceRender: true,
        children: (
          <SteelMeltAndPourCountryDetail
            fullNamePath={fullNamePath}
            localNamePath={localNamePath}
          />
        ),
      });
    }

    if (isSection232CopperEnabled) {
      const shouldShowCopperInfoTab = () =>
        isSection232CopperSmeltAndCastApplicableForAnyTariff(
          htsValues.htsObjects,
          arrivalDate ? new Date(arrivalDate) : undefined,
          skipSection232ImportDateCheck,
        );

      if (shouldShowCopperInfoTab()) {
        tabs.push({
          key: 'copperInfo',
          label: getTabTitle('Copper Info', [
            [...fullNamePath, 'copperRequirements'],
          ]),
          forceRender: true,
          children: (
            <CopperSmeltAndCastCountryDetail
              fullNamePath={fullNamePath}
              localNamePath={localNamePath}
            />
          ),
        });
      }
    }

    const line = getFieldValue(fullNamePath) ?? {};
    const allQuestions = isTariff232AutomationFeatureEnabled
      ? getMergedQuestions(
          line,
          candidateAdditionalTariffs.map((c) => c.type),
        )
      : [];
    const showProductUsageTab = allQuestions.length > 0;
    if (showProductUsageTab) {
      const hasUnanswered = allQuestions.some((q) => {
        const v = getFieldValue([...fullNamePath, ...q.entryPath]);
        return v === undefined || v === null;
      });

      tabs.push({
        key: 'productUsage',
        label: (
          <>
            {safeGetMessage(
              bundle,
              'product_usage_tab',
              undefined,
              'Product usage',
            )}{' '}
            {hasUnanswered ? <WarningOutlined /> : undefined}
          </>
        ),
        forceRender: true,
        children: (
          <ProductUsageTab
            fullNamePath={fullNamePath}
            candidateAdditionalTariffs={candidateAdditionalTariffs}
            originCountryCode={originCountryCode}
            arrivalDate={arrivalDate}
            modeOfTransport={modeOfTransport}
            readOnly={readOnly}
          />
        ),
      });
    }

    return tabs;
  }, [
    fullNamePath,
    localNamePath,
    workOrderType,
    handleRemoveTariff,
    readOnly,
    disabled,
    required,
    feeCodes,
    htsValues,
    originCountryCode,
    adTabManuallyAdded,
    getFieldValue,
    cvTabManuallyAdded,
    isPsc,
    taxClassCodes,
    keyRef,
    readWriteControlsConfig,
    allPGAFlags,
    arrivalDate,
    getTabTitle,
    shipperId,
    createdTime,
    candidateAdditionalTariffs,
    modeOfTransport,
    productCharacteristics,
    isInboundBooking,
  ]);

  // When COO is changed, remove everything but the primary code and rely on auto-add
  // to re-add anything applicable to the new COO
  useEffect(() => {
    const handleCountryChange = async () => {
      if (!htsValues.lineHtsNumber) return;
      const primaryHtsValues = tariffs.filter((tariff) =>
        isRealHts(tariff?.htsNumber),
      );
      form.setFields([
        {
          name: [...fullNamePath, 'tariffs'],
          value: primaryHtsValues,
        },
      ]);
      await addAdditionalApplicableTariffs(htsValues);
    };

    // Tariff redesign owns additional-HTS add/remove; skip legacy orchestration.
    if (isTariffRedesignEnabled) {
      return;
    }

    if (
      originCountryCode !== previousOriginCode &&
      (isP42 || accountType === AccountType.OPERATOR) &&
      !readOnly
    ) {
      void handleCountryChange();
    }
  }, [
    accountType,
    readOnly,
    form,
    tariffs,
    fullNamePath,
    htsValues,
    addAdditionalApplicableTariffs,
    originCountryCode,
    previousOriginCode,
  ]);
  const handleTabChange = useCallback(() => {
    void form.validateFields(validateFields);
  }, [validateFields, form]);

  return (
    <StyledTabContainer>
      <Tabs
        items={tabs}
        defaultActiveKey="line-info"
        onChange={handleTabChange}
      />
    </StyledTabContainer>
  );
};

export default memo(LineItemTabs);
